import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useRepairOrders } from "../../context/RepairOrdersContext";
import useDebounce from "../../../../../hooks/useDebounce";
import {
  statusColorMap,
  formatCurrency,
  formatMileage,
  formatDateDisplay,
  REPAIR_ORDER_STATUS_OPTIONS,
  DATE_PRESET_OPTIONS,
  getDateRangeForPreset,
} from "./formatters";

export const useRepairOrdersPage = () => {
  // ============================================================================
  // External Hooks
  // ============================================================================
  const { contactId } = useParams();
  const {
    baseOrders,
    baseMeta,
    orders,
    ordersMeta,
    statuses,
    models,
    loading,
    filtersLoading,
    error,
    isEmpty,
    fetchRepairOrders,
    fetchStatuses,
    fetchModels,
    resetRepairOrders,
    resetOrdersToBase,
  } = useRepairOrders();

  // ============================================================================
  // State - Filter States
  // ============================================================================
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [modelFilter, setModelFilter] = useState("");
  const [lastUpdatePreset, setLastUpdatePreset] = useState("");
  const [lastUpdateRange, setLastUpdateRange] = useState({ from: null, to: null });

  // ============================================================================
  // Debounced Search Value
  // ============================================================================
  const debouncedSearchValue = useDebounce(searchValue, 500);

  // ============================================================================
  // State - Pagination & Sorting
  // ============================================================================
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [orderBy, setOrderBy] = useState(["createdAt"]);
  const [orderDirection, setOrderDirection] = useState(["DESC"]);

  // ============================================================================
  // State - UI State (Keys and Local Searches)
  // ============================================================================
  const [searchKey, setSearchKey] = useState(0);
  const [filtersKey, setFiltersKey] = useState(0);
  const [localSearches, setLocalSearches] = useState({
    status: "",
    model: "",
    lastUpdate: "",
  });

  // ============================================================================
  // State - Internal State
  // ============================================================================
  const [hasLoadedBase, setHasLoadedBase] = useState(false);
  const prevContactIdRef = useRef(null);
  const prevSanitizedSearchRef = useRef(null);

  // ============================================================================
  // Helper Functions
  // ============================================================================
  const filterOptions = useCallback((options, searchTerm) => {
    if (!searchTerm.trim()) return options;
    const searchLower = searchTerm.toLowerCase();
    return options.filter(
      (option) =>
        option.label?.toLowerCase().includes(searchLower) ||
        option.value?.toLowerCase().includes(searchLower)
    );
  }, []);

  // ============================================================================
  // useMemo - Base Options
  // ============================================================================
  const modelOptions = useMemo(
    () =>
      models.map(({ model, model_desc }) => ({
        label: model_desc || model,
        value: model,
      })),
    [models]
  );

  // ============================================================================
  // useMemo - Filtered Options (for Select.Search)
  // ============================================================================
  const filteredStatus = useMemo(
    () => filterOptions(REPAIR_ORDER_STATUS_OPTIONS, localSearches.status),
    [localSearches.status, filterOptions]
  );

  const filteredModel = useMemo(
    () => filterOptions(modelOptions, localSearches.model),
    [modelOptions, localSearches.model, filterOptions]
  );

  const filteredLastUpdate = useMemo(
    () => filterOptions(DATE_PRESET_OPTIONS, localSearches.lastUpdate),
    [localSearches.lastUpdate, filterOptions]
  );

  // ============================================================================
  // useMemo - Derived Data (for API calls)
  // ============================================================================
  const filtersPayload = useMemo(() => {
    const payload = {};

    if (statusFilter) {
      payload.statusDescription = statusFilter;
    }

    if (modelFilter) {
      payload.model = modelFilter;
    }

    if (lastUpdateRange.from || lastUpdateRange.to) {
      payload.lastUpdate = {
        ...(lastUpdateRange.from ? { from: lastUpdateRange.from } : {}),
        ...(lastUpdateRange.to ? { to: lastUpdateRange.to } : {}),
      };
    }

    return Object.keys(payload).length ? payload : undefined;
  }, [statusFilter, modelFilter, lastUpdateRange]);

  const sanitizedSearch = useMemo(() => {
    const trimmed = debouncedSearchValue.trim();
    return trimmed.length ? trimmed : null;
  }, [debouncedSearchValue]);

  // ============================================================================
  // useMemo - Check if there are active filters
  // ============================================================================
  const hasActiveFilters = useMemo(() => {
    return Boolean(
      sanitizedSearch ||
      statusFilter ||
      modelFilter ||
      lastUpdatePreset
    );
  }, [sanitizedSearch, statusFilter, modelFilter, lastUpdatePreset]);

  // ============================================================================
  // useMemo - Data Transformations
  // ============================================================================
  const mappedOrders = useMemo(
    () =>
      orders.map((order) => ({
        id: order.serviceOrderUuid,
        vehicle:
          order.vehicle?.year && order.vehicle?.make && order.vehicle?.model
            ? `${order.vehicle.year} ${order.vehicle.make} ${order.vehicle.model}`
            : order.vehicle?.model || order.vehicle?.make || "—",
        roNumber: order.repairOrderNumber || "—",
        mileageIn: formatMileage(order.mileage ?? order.vehicle?.serviceMileage),
        statusCode: order.statusCode,
        statusDescription: order.statusDescription || order.statusCode,
        statusColor: statusColorMap[order.statusCode] || "neutral",
        appointment: formatDateDisplay(order.appointmentDate, "MMM DD YYYY"),
        opened: formatDateDisplay(order.costBreakdownOpened),
        closed: formatDateDisplay(order.costBreakdownClosed),
        customerPay: formatCurrency(order.customerPay),
        warrantyPay: formatCurrency(order.warrantyPay),
        totalRO: formatCurrency(order.totalAmount),
        serviceAdvisor: order.advisorName || "—",
        lastService: formatDateDisplay(order.lastService),
        lastUpdate: formatDateDisplay(order.lastUpdate, "MMM DD YYYY HH:mm"),
        createdVia: order.vehicle?.createdVia || "—",
        vin: order.vehicle?.vin || "—",
        vehicleUuid: order.vehicle?.vehicleUuid || "",
      })),
    [orders]
  );

  // ============================================================================
  // Utility Callbacks
  // ============================================================================
  const resetLocalState = useCallback(() => {
    setSearchValue("");
    setStatusFilter("");
    setModelFilter("");
    setLastUpdatePreset("");
    setLastUpdateRange({ from: null, to: null });
    setPage(1);
    setPageSize(20);
    setOrderBy(["createdAt"]);
    setOrderDirection(["DESC"]);
  }, []);

  const resetLocalSearches = useCallback(() => {
    setLocalSearches({
      status: "",
      model: "",
      lastUpdate: "",
    });
  }, []);

  const updateLocalSearch = useCallback((filterType, value) => {
    setLocalSearches((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  }, []);

  // ============================================================================
  // useEffect Hooks
  // ============================================================================
  useEffect(() => {
    if (!contactId) return;

    if (prevContactIdRef.current && prevContactIdRef.current !== contactId) {
      resetRepairOrders();
    }

    prevContactIdRef.current = contactId;
    setHasLoadedBase(false);
    resetLocalState();
    fetchStatuses(contactId);
    fetchModels(contactId);
  }, [contactId, fetchStatuses, fetchModels, resetRepairOrders, resetLocalState]);

  useEffect(() => {
    if (!contactId) return;

    const hasFilters = Boolean(filtersPayload || sanitizedSearch);
    const hasSorting =
      (orderBy[0] || "") !== "createdAt" || (orderDirection[0] || "") !== "DESC";
    const hasPaginationChanges = page !== 1 || pageSize !== 20;

    // Detectar cuando el search cambia de tener valor a ser null
    const searchWasCleared = 
      prevSanitizedSearchRef.current !== null && 
      sanitizedSearch === null;

    const params = {
      party_uuid: contactId,
      page,
      page_size: pageSize,
      order_by: orderBy,
      order_direction: orderDirection,
      filters: filtersPayload,
      search: sanitizedSearch,
    };

    if (!hasFilters && !hasSorting && !hasPaginationChanges) {
      if (!hasLoadedBase) {
        if (baseOrders.length) {
          resetOrdersToBase();
          setHasLoadedBase(true);
          prevSanitizedSearchRef.current = sanitizedSearch;
          return;
        }

        fetchRepairOrders(params, { persistAsBase: true });
        setHasLoadedBase(true);
        prevSanitizedSearchRef.current = sanitizedSearch;
        return;
      }

      // Si el search fue borrado y no hay otros filtros, resetear a base
      if (searchWasCleared && !filtersPayload) {
        resetOrdersToBase();
        prevSanitizedSearchRef.current = sanitizedSearch;
        return;
      }

      prevSanitizedSearchRef.current = sanitizedSearch;
      return;
    }

    fetchRepairOrders(params, { persistAsBase: false });
    prevSanitizedSearchRef.current = sanitizedSearch;
  }, [
    contactId,
    page,
    pageSize,
    orderBy,
    orderDirection,
    filtersPayload,
    sanitizedSearch,
    fetchRepairOrders,
    hasLoadedBase,
    baseOrders.length,
    resetOrdersToBase,
  ]);

  // ============================================================================
  // Handlers - Filter Handlers
  // ============================================================================
  const handleStatusChange = useCallback((value) => {
    setStatusFilter(value || "");
    setPage(1);
  }, []);

  const handleModelChange = useCallback((value) => {
    setModelFilter(value || "");
    setPage(1);
  }, []);

  const handleLastUpdatePresetChange = useCallback((value) => {
    console.log("value", value);
    setLastUpdatePreset(value || "");
    setLastUpdateRange(getDateRangeForPreset(value || ""));
    setPage(1);
  }, []);

  // ============================================================================
  // Handlers - Search Handlers
  // ============================================================================
  const handleSearchChange = useCallback((value) => {
    setSearchValue(value);
  }, []);

  // ============================================================================
  // Handlers - Clear Handlers
  // ============================================================================
  const handleClearFilters = useCallback(() => {
    setSearchValue("");
    setStatusFilter("");
    setModelFilter("");
    setLastUpdatePreset("");
    setLastUpdateRange({ from: null, to: null });
    setPage(1);
    resetOrdersToBase();
  }, [resetOrdersToBase]);

  const handleClearFiltersWithKeys = useCallback(() => {
    resetLocalSearches();
    setSearchKey((prev) => prev + 1);
    setFiltersKey((prev) => prev + 1);
    handleClearFilters();
  }, [resetLocalSearches, handleClearFilters]);

  // ============================================================================
  // Handlers - Pagination Handlers
  // ============================================================================
  const handlePageChange = useCallback(
    (nextPage) => {
      setPage(nextPage);

      if (nextPage === 1) {
        const hasFilters = Boolean(
          statusFilter ||
            modelFilter ||
            lastUpdateRange.from ||
            lastUpdateRange.to ||
            sanitizedSearch
        );
        const isDefaultSorting =
          (orderBy[0] || "") === "createdAt" && (orderDirection[0] || "") === "DESC";

        if (!hasFilters && isDefaultSorting) {
          resetOrdersToBase();
        }
      }
    },
    [
      statusFilter,
      modelFilter,
      lastUpdateRange.from,
      lastUpdateRange.to,
      sanitizedSearch,
      orderBy,
      orderDirection,
      resetOrdersToBase,
    ]
  );

  const handleItemsPerPageChange = useCallback(
    (itemsPerPage) => {
      setPageSize(itemsPerPage);
      setPage(1);

      if (itemsPerPage === 20) {
        const hasFilters = Boolean(
          statusFilter ||
            modelFilter ||
            lastUpdateRange.from ||
            lastUpdateRange.to ||
            sanitizedSearch
        );
        const isDefaultSorting =
          (orderBy[0] || "") === "createdAt" && (orderDirection[0] || "") === "DESC";

        if (!hasFilters && isDefaultSorting) {
          resetOrdersToBase();
        }
      }
    },
    [
      statusFilter,
      modelFilter,
      lastUpdateRange.from,
      lastUpdateRange.to,
      sanitizedSearch,
      orderBy,
      orderDirection,
      resetOrdersToBase,
    ]
  );

  // ============================================================================
  // Handlers - Refresh Handler
  // ============================================================================
  const handleRefresh = useCallback(() => {
    if (!contactId) return;
    
    const params = {
      party_uuid: contactId,
      page,
      page_size: pageSize,
      order_by: orderBy,
      order_direction: orderDirection,
      filters: filtersPayload,
      search: sanitizedSearch,
    };
    
    fetchRepairOrders(params);
  }, [
    contactId,
    page,
    pageSize,
    orderBy,
    orderDirection,
    filtersPayload,
    sanitizedSearch,
    fetchRepairOrders,
  ]);

  // ============================================================================
  // Return
  // ============================================================================
  return {
    loading,
    filtersLoading,
    error,
    isEmpty,
    orders: mappedOrders,
    ordersMeta,
    filters: {
      statusOptions: REPAIR_ORDER_STATUS_OPTIONS,
      modelOptions,
      lastUpdateOptions: DATE_PRESET_OPTIONS,
      filteredStatus,
      filteredModel,
      filteredLastUpdate,
      selectedStatus: statusFilter,
      selectedModel: modelFilter,
      selectedLastUpdatePreset: lastUpdatePreset,
      searchValue,
      hasActiveFilters,
    },
    filterHandlers: {
      handleStatusChange,
      handleModelChange,
      handleLastUpdatePresetChange,
      handleSearchChange,
      handleClearFiltersWithKeys,
    },
    filterUI: {
      searchKey,
      filtersKey,
      updateLocalSearch,
    },
    handlePageChange,
    handleItemsPerPageChange,
    handleRefresh,
  };
};

